import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import type { TableLazyLoadEvent } from 'primeng/types/table';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { PropertyAdminService } from '../../../core/services/property-admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { PropertyResponse, PageResponse } from '../../../core/models';
import { AppEmptyState } from '../../../shared/ui/empty-state/app-empty-state';
import { AppStatusTag } from '../../../shared/ui/status-tag/app-status-tag';
import { FcfaPipe } from '../../../shared/pipes/fcfa.pipe';
import { PropertyKanbanComponent } from '../property-kanban/property-kanban';
import { PROPERTY_TYPES, propertyStatusLabel, propertyStatusSeverity, propertyTypeLabel } from '../property-labels';

const EMPTY_PAGE: PageResponse<PropertyResponse> = { content: [], totalElements: 0, totalPages: 0, number: 0, size: 20 };

type ViewMode = 'list' | 'kanban';
const VIEW_MODE_KEY = 'bo.properties.viewMode';

@Component({
  selector: 'app-property-list',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, TabsModule, ButtonModule,
    TableModule, InputTextModule, SelectModule, ProgressSpinnerModule, TooltipModule,
    SelectButtonModule, AppEmptyState, AppStatusTag, FcfaPipe, PropertyKanbanComponent],
  templateUrl: './property-list.html',
  styleUrl: './property-list.scss'
})
export class PropertyListComponent implements OnInit {
  private svc   = inject(PropertyAdminService);
  private toast = inject(MessageService);
  private auth  = inject(AuthService);

  readonly canManageTrash = this.auth.hasPermission('PROPERTIES_DELETE');

  // ── État global ──────────────────────────────────────────────────────────────
  activeTab  = signal('0');
  typeFilter = signal('');
  searchAll  = signal('');
  viewMode   = signal<ViewMode>(this.readViewMode());

  readonly viewOptions = [
    { value: 'list',   icon: 'pi pi-list',     tooltip: 'Liste' },
    { value: 'kanban', icon: 'pi pi-th-large', tooltip: 'Kanban par étape' }
  ];

  readonly typeOptions = [{ label: 'Tous les types', value: '' }, ...PROPERTY_TYPES.map(t => ({ label: propertyTypeLabel(t), value: t }))];

  // ── Tab "Tous les biens" ─────────────────────────────────────────────────────
  loadingAll   = signal(true);
  pageAll      = signal<PageResponse<PropertyResponse>>(EMPTY_PAGE);
  pageSizeAll  = 20;
  pageIndexAll = 0;

  // ── Tab "En attente" ─────────────────────────────────────────────────────────
  loadingPending   = signal(true);
  pagePending      = signal<PageResponse<PropertyResponse>>(EMPTY_PAGE);
  pageSizePending  = 20;
  pageIndexPending = 0;

  // ── Tab "Corbeille" (PROPERTIES_DELETE) ─────────────────────────────────────
  loadingDeleted   = signal(true);
  pageDeleted      = signal<PageResponse<PropertyResponse>>(EMPTY_PAGE);
  pageSizeDeleted  = 20;
  pageIndexDeleted = 0;
  restoring        = signal<string | null>(null);

  // Filtre local (recherche + type) appliqué à la page actuellement chargée —
  // le backend n'exposant pas de recherche plein texte ni de filtre par type.
  filteredAll = computed(() => {
    const term = this.searchAll().trim().toLowerCase();
    const type = this.typeFilter();
    return this.pageAll().content.filter(p => {
      const matchesTerm = !term || p.title?.toLowerCase().includes(term) || p.city?.toLowerCase().includes(term) || p.commune?.toLowerCase().includes(term);
      const matchesType = !type || p.type === type;
      return matchesTerm && matchesType;
    });
  });

  ngOnInit() {
    this.loadAll();
    this.loadPending();
    if (this.canManageTrash) this.loadDeleted();
  }

  // ── Chargements ──────────────────────────────────────────────────────────────

  private loadAll() {
    this.loadingAll.set(true);
    this.svc.getAllProperties(this.pageIndexAll, this.pageSizeAll)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res) this.pageAll.set(res);
        this.loadingAll.set(false);
      });
  }

  private loadPending() {
    this.loadingPending.set(true);
    this.svc.getPendingProperties(this.pageIndexPending, this.pageSizePending)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res) this.pagePending.set(res);
        this.loadingPending.set(false);
      });
  }

  private loadDeleted() {
    this.loadingDeleted.set(true);
    this.svc.getDeletedProperties(this.pageIndexDeleted, this.pageSizeDeleted)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (res) this.pageDeleted.set(res);
        this.loadingDeleted.set(false);
      });
  }

  // ── Événements ───────────────────────────────────────────────────────────────

  onLazyDeleted(e: TableLazyLoadEvent) {
    const rows = e.rows ?? this.pageSizeDeleted;
    this.pageSizeDeleted = rows;
    this.pageIndexDeleted = Math.floor((e.first ?? 0) / rows);
    this.loadDeleted();
  }

  restore(p: PropertyResponse) {
    this.restoring.set(p.id);
    this.svc.restoreProperty(p.id).subscribe({
      next: () => {
        this.restoring.set(null);
        this.toast.add({ severity: 'success', summary: 'Bien restauré', detail: p.title, life: 3500 });
        this.loadDeleted();
        this.loadAll();
        this.loadPending();
      },
      error: err => {
        this.restoring.set(null);
        this.toast.add({ severity: 'error', summary: 'Restauration impossible',
          detail: err?.error?.message ?? 'La restauration a échoué.', life: 5000 });
      }
    });
  }

  onSearchChange(value: string) { this.searchAll.set(value); }
  clearSearch() { this.searchAll.set(''); }

  onLazyAll(e: TableLazyLoadEvent) {
    const rows = e.rows ?? this.pageSizeAll;
    this.pageSizeAll = rows;
    this.pageIndexAll = Math.floor((e.first ?? 0) / rows);
    this.loadAll();
  }

  onLazyPending(e: TableLazyLoadEvent) {
    const rows = e.rows ?? this.pageSizePending;
    this.pageSizePending = rows;
    this.pageIndexPending = Math.floor((e.first ?? 0) / rows);
    this.loadPending();
  }

  onTabChange(value: string | number | undefined) {
    this.activeTab.set(String(value ?? '0'));
  }

  onViewModeChange(mode: ViewMode | null) {
    if (!mode) return; // SelectButton émet null quand on re-clique l'option active
    this.viewMode.set(mode);
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* SSR ou stockage bloqué */ }
  }

  private readViewMode(): ViewMode {
    try { return localStorage.getItem(VIEW_MODE_KEY) === 'kanban' ? 'kanban' : 'list'; } catch { return 'list'; }
  }

  // ── Formatage ────────────────────────────────────────────────────────────────

  statusLabel    = propertyStatusLabel;
  statusSeverity = propertyStatusSeverity;
  typeLabel      = propertyTypeLabel;

  formatDate(d: string)  { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
}
